import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, Alert } from "@mui/material";
import { jwtVerify } from "jose";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useMutation } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import CartSummary from "../../components/Cart";
import CountdownTimer from "../../components/CountdownTimer";
import MainLayout from "../../components/MainLayout";
import { useAppDispatch } from "../../hooks/useDispatch";
import useNotification from "../../hooks/useNotification";
import categoryService from "../../services/category";
import service from "../../services/event";
import voucherService from "../../services/voucher";
import { deleteByEventId } from "../../store/reducers/cart-slice";
import queryDefault from "../../utils/queryDefault";
import DynamicBuyerInformationForm from "../../components/DynamicBuyerInformationForm";
import { detectEmailTypo } from "../../utils/emailTypoDetector";

interface EventDetails {
  id: string;
  name: string;
  description: string;
  photo: string;
  is_persen: boolean;
  ppn: number;
  include_tax: boolean;
  fee: number;
  start_date: string;
  end_date: string;
  location: string;
  slug: string;
  checkout_time?: number;
  is_voucher_mandatory?: boolean;
  transaction_fee_is_persen: boolean;
  transaction_fee: number;
}

export default function Checkout() {
  const notification = useNotification();
  const [timeLeft, setTimeLeft] = useState<number>(0); // Time left in seconds
  const navigate = useNavigate();
  const location = useLocation();
  const [voucher, setVoucher] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [diskon, setDiskon] = useState(0);
  const [total, setTotal] = useState(0);
  const dispatch = useAppDispatch();
  const [slug, setSlug] = useState("");
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalTickets, setTotalTickets] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<any>(null);
  const [typoWarningOpen, setTypoWarningOpen] = useState(false);
  const [emailTypoInfo, setEmailTypoInfo] = useState<any>(null);

  const onRemoveVoucher = () => {
    setVoucher("");
    setDiskon(0);
    setError("");
    setTotal(totalPrice);
  };

  const [eventDetails, setEventDetails] = useState<EventDetails>({
    id: "",
    name: "",
    description: "",
    photo: "",
    fee: 0,
    include_tax: false,
    is_persen: false,
    ppn: 0,
    location: "",
    start_date: "",
    end_date: "",
    slug: "",
    is_voucher_mandatory: false,
    transaction_fee_is_persen: false,
    transaction_fee: 0,
  });

  useEffect(() => {
    const validateTokenAndFetchEvent = async () => {
      const storedToken = sessionStorage.getItem("formToken");
      const stateToken = location.state?.token;

      try {
        if (!storedToken || storedToken !== stateToken) {
          throw new Error("Token is missing or invalid.");
        }

        const SECRET_KEY = new TextEncoder().encode(
          process.env.REACT_APP_SECRET
        );
        const { payload } = await jwtVerify(storedToken, SECRET_KEY);

        if (payload && payload.exp) {
          const currentTime = Math.floor(Date.now() / 1000);
          const timeLeftSeconds = payload.exp - currentTime;
          setTimeLeft(timeLeftSeconds);
        }

        const { event_id, filteredItems, totalTickets, totalPrice } =
          payload as unknown as {
            event_id: string;
            filteredItems: never[];
            totalTickets: number;
            totalPrice: number;
          };

        setSlug(event_id);
        setFilteredItems(filteredItems || []);
        setTotalTickets(totalTickets || 0);
        setTotalPrice(totalPrice || 0);

        const response = await service.viewPublicEventDetail({
          id: event_id,
          is_category: false,
        });
        setEventDetails(response.data);
      } catch (error) {
        notification.onOpen({
          message: "Invalid token",
          position: "top",
          type: "error",
        });
        console.error("Token validation or event fetch failed:", error);
        navigate("/");
      }
    };

    validateTokenAndFetchEvent();
  }, [location.state, navigate, dispatch, slug]);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          sessionStorage.removeItem("formToken"); // Remove token
          dispatch(deleteByEventId(slug)); // delete cart item
          navigate("/"); // Redirect to home
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer); // Cleanup on unmount
  }, [timeLeft, navigate]);

  useEffect(() => {
    setTotal(totalPrice);
  }, [totalPrice]);

  const onClickUseVoucher = () => {
    setError("");
    voucherService
      .getVoucherByCode({
        code: voucher,
        event_id: eventDetails.id,
        category_id: filteredItems[0].category_id,
      })
      .then((response) => {
        let disc = 0;
        if (response.data.is_persen) {
          disc = Math.min(
            (response.data.amount * totalPrice) / 100,
            response.data.max_amount,
            totalPrice
          );
          setDiskon(disc);
        } else {
          disc = Math.min(response.data.amount, totalPrice);
          setDiskon(disc);
        }
        setTotal(totalPrice - disc);

        // Show success notification even if discount is 0
        notification.onOpen({
          message: disc > 0 ? `Voucher applied! Discount: ${disc.toLocaleString('id-ID', { style: 'currency', currency: 'IDR' })}` : "Voucher is valid",
          type: "success",
          position: "top",
        });
      })
      .catch((error) => {
        const err = get(error, "response.data.message");
        setError(err);
        notification.onOpen({
          message: err || "Invalid voucher code",
          type: "error",
          position: "top",
        });
      });
  };

  const checkout = useMutation("checkout", categoryService.checkout, {
    onSuccess: (data, variables) => {
      navigate("/payment-redirect", {
        state: {
          payment_url: data.payment_url
        }
      });
      dispatch(deleteByEventId(slug));
      setIsLoading(false);
      notification.onOpen({
        message: "Redirecting to payment...",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      setIsLoading(false);
      notification.onOpen({
        message:
          error?.response?.data?.error !== ""
            ? error.response.data.error
            : "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });
  const onSubmit = (values: any) => {
    setPendingFormValues(values);
    
    // Check for email typos
    const email = values?.orderInfo?.email;
    if (email) {
      const typoResult = detectEmailTypo(email);
      if (typoResult.hasTypo) {
        setEmailTypoInfo(typoResult);
        setTypoWarningOpen(true);
        return;
      }
    }
    
    setConfirmDialogOpen(true);
  };

  const handleProceedWithTypo = () => {
    setTypoWarningOpen(false);
    setConfirmDialogOpen(true);
  };

  const handleFixTypo = () => {
    setTypoWarningOpen(false);
    setPendingFormValues(null);
    setEmailTypoInfo(null);
  };

  const handleConfirmCheckout = () => {
    setConfirmDialogOpen(false);
    setIsLoading(true);
    const detail = filteredItems.map((item: any) => ({
      category_id: item.category_id,
      quantity: item.quantity,
      category_name: item.category_name,
    }));

    const data = {
      orderInfo: pendingFormValues.orderInfo,
      passengers: pendingFormValues.passengers,
      is_news: pendingFormValues.is_news,
      is_aggree: pendingFormValues.is_aggree,
      detail,
      voucher_code: voucher,
      event_id: eventDetails.id,
    };
    checkout.mutate(data);
  };

  const handleCancelCheckout = () => {
    setConfirmDialogOpen(false);
    setPendingFormValues(null);
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Checkout Page</title>
      </Helmet>
      <Breadcrumb
        label={`Checkout - ${eventDetails.name}`}
        breadcrumbs={[
          { label: eventDetails.name, href: `/event/${eventDetails.slug}` },
          { label: "Select Tickets" },
          { label: "Checkout" },
        ]}
      />

      <CountdownTimer timeLeft={timeLeft} totalTime={eventDetails.checkout_time || 10} />

      <Box sx={{ mt: 2 }}>
        <Typography variant="body1" color="textSecondary" mb={3}>
          {new Date(eventDetails.start_date).toLocaleString()} -{" "}
          {new Date(eventDetails.end_date).toLocaleString()}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            {eventDetails.slug && (
              <DynamicBuyerInformationForm
                eventSlug={eventDetails.slug}
                totalTickets={totalTickets}
                cartItems={filteredItems}
                onSubmit={onSubmit}
                isLoading={isLoading}
                isVoucherMandatory={eventDetails.is_voucher_mandatory || false}
                voucherApplied={voucher ? true : false}
              />
            )}
          </Grid>
          <Grid item xs={12} md={5}>
            <CartSummary
              filteredItems={filteredItems}
              totalTickets={totalTickets}
              totalPrice={totalPrice}
              voucher={voucher}
              error={error}
              discount={diskon}
              total={total}
              eventDetails={eventDetails}
              onVoucherChange={(value) => setVoucher(value)}
              onRemoveVoucher={onRemoveVoucher}
              onApplyVoucher={onClickUseVoucher}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Email Typo Warning Dialog */}
      <Dialog 
        open={typoWarningOpen} 
        onClose={handleFixTypo}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'warning.main' }}>⚠️ Possible Email Typo Detected</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            We noticed a possible typo in your email address.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You entered:
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'error.main' }}>
            {emailTypoInfo?.originalEmail}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {emailTypoInfo?.description}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Suggested correction:
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
            {emailTypoInfo?.suggestedEmail}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your eTickets and invoice will be sent to this email address. Please make sure it's correct.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleFixTypo} variant="contained" color="primary">
            Go Back & Fix
          </Button>
          <Button 
            onClick={handleProceedWithTypo} 
            variant="outlined"
            color="warning"
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialogOpen} 
        onClose={handleCancelCheckout}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Order</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your eTicket and invoice will be sent to:
          </Typography>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            {pendingFormValues?.orderInfo?.email}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleCancelCheckout} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmCheckout} 
            variant="contained" 
            autoFocus
          >
            Proceed to Payment
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
